import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";

// Multi-filters for every task view (board, list, calendar; tasks and projects).
// Categories AND together, the values inside one category OR together.

export const QUICK_FILTERS = [
    {id: "remaining", label: "کارهای باقی‌مانده"},
    {id: "overdue", label: "کارهای عقب‌افتاده"},
];

export const FILTER_CATEGORIES = [
    {id: "due_date", label: "تاریخ سررسید", icon: "event", kind: "date", field: "dueDate"},
    {id: "assignee", label: "مسئول انجام کار", icon: "person", kind: "people", searchable: true},
    {id: "collaborators", label: "اعضای درگیر", icon: "groups", kind: "people", searchable: true},
    {id: "creator", label: "ایجاد شده توسط", icon: "person_add", kind: "people", searchable: true},
    {id: "created_at", label: "ایجاد شده در", icon: "add_circle", kind: "date", field: "createdAt"},
    {id: "edited_at", label: "آخرین ویرایش در", icon: "edit_calendar", kind: "date", field: "updatedAt"},
    {id: "project", label: "پروژه‌ها", icon: "folder_open", searchable: true},
    {id: "status", label: "وضعیت کار", icon: "check_circle"},
    {id: "milestone", label: "نقطه عطف", icon: "flag", searchable: true},
    {id: "priority", label: "اولویت", icon: "warning"},
    {id: "tags", label: "برچسب‌ها", icon: "sell", searchable: true},
    {id: "task_type", label: "نوع کار", icon: "category"},
];

export const DATE_OPTIONS = [
    {id: "today", label: "امروز", icon: "today"},
    {id: "yesterday", label: "دیروز", icon: "history"},
    {id: "this_week", label: "این هفته", icon: "date_range"},
    {id: "last_week", label: "هفته گذشته", icon: "event_busy"},
    {id: "last_14_days", label: "14 روز گذشته", icon: "history_toggle_off"},
    {id: "last_month", label: "ماه گذشته", icon: "calendar_month"},
    {id: "custom_date", label: "انتخاب تاریخ", icon: "edit_calendar"},
];
// a due date is usually ahead, so it also offers the next week
const DUE_DATE_EXTRA = [{id: "next_week", label: "هفته آینده", icon: "event_upcoming"}];

export const STATUS_OPTIONS = [
    {id: "todo", label: "برای انجام"},
    {id: "doing", label: "در حال انجام"},
    {id: "blocked", label: "مسدود"},
    {id: "done", label: "انجام شده"},
];

export const PRIORITY_OPTIONS = [
    {id: "high", label: "بالا", color: "#ef4444"},
    {id: "medium", label: "متوسط", color: "#f59e0b"},
    {id: "low", label: "پایین", color: "#22c55e"},
];

export const NONE = "none";

export function createTaskListFilters() {
    return {quick: null, values: {}, customRanges: {}, search: ""};
}

export function getTaskListFilterCount(filters) {
    if (!filters) return 0;
    return Object.values(filters.values ?? {}).filter((values) => values?.length).length
        + (filters.quick ? 1 : 0)
        + (filters.search?.trim() ? 1 : 0);
}

const keyOf = (item) => {
    const key = item?.documentId ?? item?.id;
    return key === null || key === undefined ? null : String(key);
};
const sameKey = (item, key) => item && (String(item.documentId) === key || String(item.id) === key);
const profileOf = (membership) => membership?.member ?? membership;
export const personName = (membership) => {
    const profile = profileOf(membership);
    return profile?.fullname || profile?.username || profile?.email || "";
};

const startOfDay = (date) => {
    const value = new Date(date);
    value.setHours(0, 0, 0, 0);
    return value;
};
const endOfDay = (date) => {
    const value = new Date(date);
    value.setHours(23, 59, 59, 999);
    return value;
};
const addDays = (date, days) => {
    const value = new Date(date);
    value.setDate(value.getDate() + days);
    return value;
};

// the Persian week starts on Saturday
function weekRange(now, offset) {
    const start = startOfDay(addDays(now, -((now.getDay() + 1) % 7) + offset * 7));
    return {start, end: endOfDay(addDays(start, 6))};
}

function lastPersianMonth(now) {
    const first = new DateObject({date: now, calendar: persian}).toFirstOfMonth().subtract(1, "month");
    const start = startOfDay(first.toDate());
    const end = endOfDay(new DateObject({date: start, calendar: persian}).toLastOfMonth().toDate());
    return {start, end};
}

export function toNativeDate(value) {
    if (!value) return null;
    const date = typeof value.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function dateOptionRange(option, now = new Date(), customRange) {
    switch (option) {
        case "today": return {start: startOfDay(now), end: endOfDay(now)};
        case "yesterday": return {start: startOfDay(addDays(now, -1)), end: endOfDay(addDays(now, -1))};
        case "this_week": return weekRange(now, 0);
        case "last_week": return weekRange(now, -1);
        case "next_week": return weekRange(now, 1);
        case "last_14_days": return {start: startOfDay(addDays(now, -13)), end: endOfDay(now)};
        case "last_month": return lastPersianMonth(now);
        case "custom_date": {
            let start = toNativeDate(customRange?.start);
            let end = toNativeDate(customRange?.end);
            if (!start && !end) return null;
            if (start && end && start > end) [start, end] = [end, start];
            return {start: start ? startOfDay(start) : null, end: end ? endOfDay(end) : null};
        }
        default: return null;
    }
}

export function isTaskOverdue(task, now = new Date()) {
    if (!task?.dueDate || task.taskStatus === "done") return false;
    const due = new Date(task.dueDate);
    return !Number.isNaN(due.getTime()) && due < now;
}

const taskPeople = (task, category) => {
    if (category === "assignee") return task.assignee ? [task.assignee] : [];
    if (category === "creator") return task.creatorUser ? [task.creatorUser] : [];
    return (task.members ?? []).filter(Boolean);
};
const taskTags = (task) => (Array.isArray(task.tags) ? task.tags : []).map((tag) => String(tag));

function matchesCategory(task, category, values, customRange, now) {
    const definition = FILTER_CATEGORIES.find((item) => item.id === category);
    if (!definition) return true;
    if (definition.kind === "date") {
        const date = toNativeDate(task[definition.field]);
        if (!date) return false;
        return values.some((option) => {
            const range = dateOptionRange(option, now, customRange);
            return range && (!range.start || date >= range.start) && (!range.end || date <= range.end);
        });
    }
    if (definition.kind === "people") {
        const people = taskPeople(task, category);
        return values.some((key) => key === NONE ? people.length === 0 : people.some((person) => sameKey(person, key)));
    }
    switch (category) {
        case "project":
            return values.some((key) => key === NONE ? !task.projects?.length : (task.projects ?? []).some((project) => sameKey(project, key)));
        case "milestone":
            return values.some((key) => key === NONE ? !task.deliverables?.length : (task.deliverables ?? []).some((deliverable) => sameKey(deliverable, key)));
        case "status":
            return values.includes(task.taskStatus || "todo");
        case "priority":
            return values.includes(task.priority || NONE);
        case "tags":
            return values.some((tag) => tag === NONE ? taskTags(task).length === 0 : taskTags(task).includes(tag));
        case "task_type":
            return values.some((key) => key === NONE ? !task.task_type : sameKey(task.task_type, key));
        default:
            return true;
    }
}

export function filterTaskList(items, filters, _statusFilter, _showDateFilter, now = new Date()) {
    const current = filters ?? createTaskListFilters();
    const search = current.search?.trim().toLocaleLowerCase();
    const active = Object.entries(current.values ?? {}).filter(([, values]) => values?.length);
    return (items ?? []).filter((task) => {
        if (!task) return false;
        if (search && !String(task.title ?? "").toLocaleLowerCase().includes(search)) return false;
        if (current.quick === "remaining" && task.taskStatus === "done") return false;
        if (current.quick === "overdue" && !isTaskOverdue(task, now)) return false;
        return active.every(([category, values]) => matchesCategory(task, category, values, current.customRanges?.[category], now));
    });
}

const uniqueBy = (items, key) => [...new Map(items.filter((item) => key(item) !== null).map((item) => [key(item), item])).values()];

// the values each category offers, built from the tasks on screen and the workspace members
export function categoryOptions(category, tasks = [], workspaceMemberships = []) {
    const definition = FILTER_CATEGORIES.find((item) => item.id === category);
    if (definition?.kind === "date") return category === "due_date" ? [...DATE_OPTIONS.slice(0, 4), ...DUE_DATE_EXTRA, ...DATE_OPTIONS.slice(4)] : DATE_OPTIONS;
    if (definition?.kind === "people") {
        const fromTasks = tasks.flatMap((task) => taskPeople(task, category));
        const people = uniqueBy([...workspaceMemberships, ...fromTasks].filter((person) => personName(person)), keyOf)
            .map((person) => ({id: keyOf(person), label: personName(person), person: profileOf(person)}))
            .sort((left, right) => left.label.localeCompare(right.label, "fa"));
        if (category === "creator") return people;
        return [...people, {id: NONE, label: category === "assignee" ? "بدون مسئول" : "بدون عضو درگیر", person: null}];
    }
    switch (category) {
        case "project":
            return [...uniqueBy(tasks.flatMap((task) => task.projects ?? []), keyOf).map((project) => ({id: keyOf(project), label: project.title || project.name || "—"})), {id: NONE, label: "بدون پروژه"}];
        case "milestone":
            return [...uniqueBy(tasks.flatMap((task) => task.deliverables ?? []), keyOf).map((deliverable) => ({id: keyOf(deliverable), label: deliverable.title || "—"})), {id: NONE, label: "بدون نقطه عطف"}];
        case "status":
            return STATUS_OPTIONS;
        case "priority":
            return [...PRIORITY_OPTIONS, {id: NONE, label: "بدون اولویت"}];
        case "tags":
            return [...[...new Set(tasks.flatMap(taskTags))].sort((left, right) => left.localeCompare(right, "fa")).map((tag) => ({id: tag, label: `#${tag}`})), {id: NONE, label: "بدون برچسب"}];
        case "task_type":
            return [...uniqueBy(tasks.map((task) => task.task_type).filter(Boolean), keyOf).map((type) => ({id: keyOf(type), label: type.title, icon: type.icon})), {id: NONE, label: "بدون نوع"}];
        default:
            return [];
    }
}
